import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectInstructorId } from "../../../features/authentication/authenticationSlice";
import { LANGUAGE_OPTIONS } from "../../../features/localization/languages";
import { selectOrganization } from "../../../features/organization/organization-slice";
import MemberInstructorSelect from "../../../features/members/components/MemberInstructorSelect";
import { Group } from "../../../features/members/models/Group";
import { Member } from "../../../features/members/models/Member";
import { saveMember } from "../../../features/members/utils/saveMember";
import { ROUTES } from "../../../routes";
import UnsavedChangesGuard from "../../unsaved-changes/UnsavedChangesGuard";
import Input from "../../input/Input";
import Textarea from "../../textarea/Textarea";
import GroupEnrollmentField from "./GroupEnrollmentField";

interface MemberFormProps {
  /** The member being edited; null = create mode. */
  member: Member | null;
}

/**
 * The member create/edit form: identity fields, group enrollment and notes as
 * house cards, with an unsaved-changes guard and the save orchestration
 * delegated to `saveMember`.
 */
const MemberForm: React.FC<MemberFormProps> = ({ member }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const viewerInstructorId = useAppSelector(selectInstructorId);
  // The organization's language prefills a new member. Until it loads the field is
  // empty, and an empty language is stored as the organization's by the API.
  const defaultLanguage = useAppSelector(selectOrganization)?.languageCode ?? "";

  const isEditMode = member !== null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  // New members default to the organization's language; emails to the member
  // (schedule changes, payment reminders, portal sign-in) are written in it.
  const [primaryLanguageCode, setPrimaryLanguageCode] = useState(() =>
    isEditMode ? "" : defaultLanguage,
  );
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  // On create the API always assigns the creator, so the select defaults to
  // the signed-in instructor; edit mode is populated from the member.
  const [instructorId, setInstructorId] = useState<string | null>(() =>
    isEditMode ? null : viewerInstructorId ?? null,
  );
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [firstNameError, setFirstNameError] = useState<string | null>(null);

  // Set right before an intentional leave (post-save redirect) so the
  // unsaved-changes guard doesn't prompt on the way out.
  const leaveApprovedRef = useRef(false);

  // The organization may load after the form: adopt its language unless the
  // instructor already picked another one.
  const previousDefaultRef = useRef(defaultLanguage);
  useEffect(() => {
    const previousDefault = previousDefaultRef.current;
    if (!isEditMode && previousDefault !== defaultLanguage) {
      setPrimaryLanguageCode((current) =>
        current === previousDefault ? defaultLanguage : current,
      );
    }
    previousDefaultRef.current = defaultLanguage;
  }, [defaultLanguage, isEditMode]);

  // Populate from the loaded member in edit mode.
  useEffect(() => {
    if (member) {
      setFirstName(member.firstName || "");
      setLastName(member.lastName || "");
      setEmail(member.email || "");
      setPrimaryLanguageCode(member.primaryLanguageCode ?? "");
      setAddress(member.address || "");
      setNotes(member.notes || "");
      setInstructorId(member.instructorId);
      setSelectedGroups(member.groups || []);
    }
  }, [member]);

  /** Compares the form against its pristine state (loaded member, or blanks). */
  const hasUnsavedChanges = (): boolean => {
    const groupIdsOf = (groups: Group[]) => groups.map((g) => g.id).sort().join(",");
    const pristine = member
      ? {
          firstName: member.firstName || "",
          lastName: member.lastName || "",
          email: member.email || "",
          primaryLanguageCode: member.primaryLanguageCode ?? "",
          address: member.address || "",
          notes: member.notes || "",
          instructorId: member.instructorId,
          groupIds: groupIdsOf(member.groups || []),
        }
      : {
          firstName: "", lastName: "", email: "",
          primaryLanguageCode: defaultLanguage,
          address: "", notes: "",
          instructorId: viewerInstructorId ?? null, groupIds: "",
        };
    return (
      firstName !== pristine.firstName ||
      lastName !== pristine.lastName ||
      email !== pristine.email ||
      primaryLanguageCode !== pristine.primaryLanguageCode ||
      address !== pristine.address ||
      notes !== pristine.notes ||
      instructorId !== pristine.instructorId ||
      groupIdsOf(selectedGroups) !== pristine.groupIds
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!firstName.trim()) {
      setFirstNameError(t("members.errors.firstNameRequired"));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await saveMember(dispatch,
        { firstName, lastName, email, primaryLanguageCode, address, notes, instructorId, selectedGroups },
        member);
      if (!result.ok) {
        toast.error(t(isEditMode ? "members.errors.updateFailed" : "members.errors.createFailed"));
        return;
      }
      if (result.assignFailed) {
        toast.error(t("members.assignInstructorFailed"));
      }
      toast.success(t(isEditMode ? "members.memberUpdated" : "members.memberCreated"));
      leaveApprovedRef.current = true;
      navigate(ROUTES.MEMBER_DETAILS.replace(":id", result.memberId));
    } catch (error) {
      console.error("Error saving member:", error);
      toast.error(t("members.unexpectedError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(member ? ROUTES.MEMBER_DETAILS.replace(":id", member.id) : ROUTES.MEMBERS);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
      <UnsavedChangesGuard
        when={() => !leaveApprovedRef.current && !isSubmitting && hasUnsavedChanges()}
      />

      <div className="with-border rounded-2xl p-4 lg:p-6 space-y-4 lg:space-y-5">
        <h3>{t("members.memberInfo")}</h3>
        <Input
          label={t("members.firstName")}
          type="text"
          name="first_name"
          id="first_name"
          placeholder={t("members.firstNamePlaceholder")}
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            if (firstNameError) setFirstNameError(null);
          }}
          error={firstNameError ?? undefined}
          disabled={isSubmitting}
        />
        <Input
          label={t("members.lastName")}
          type="text"
          name="last_name"
          id="last_name"
          placeholder={t("members.lastNamePlaceholder")}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          disabled={isSubmitting}
        />
        <Input
          label={t("members.email")}
          type="email"
          name="email"
          id="email"
          placeholder={t("members.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
        />
        {/* Emails to the member (schedule changes, reminders, portal sign-in) speak this language */}
        <div className="w-full">
          <label htmlFor="member_language" className="label">
            {t("members.language")}
          </label>
          <select
            id="member_language"
            name="member_language"
            className="input w-full"
            value={primaryLanguageCode}
            onChange={(e) => setPrimaryLanguageCode(e.target.value)}
            disabled={isSubmitting}
          >
            {LANGUAGE_OPTIONS.map((language) => (
              <option key={language.code} value={language.code}>
                {language.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-grey-50 dark:text-grey-70">
            {t("members.languageHint")}
          </p>
        </div>
        {/* Printed on invoices / receipts; optional for small amounts */}
        <Textarea
          label={t("members.addressOptional")}
          name="member_address"
          id="member_address"
          placeholder={t("members.addressPlaceholder")}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          disabled={isSubmitting}
        />
        {/* Assigned instructor — only in a multi-instructor organization */}
        <MemberInstructorSelect
          value={instructorId}
          onChange={setInstructorId}
          allowUnassigned={isEditMode && (member?.instructorId ?? null) === null}
          disabled={isSubmitting}
        />
      </div>

      <div className="with-border rounded-2xl p-4 lg:p-6 space-y-4">
        <h3>{t("members.groupEnrollmentCount", { count: selectedGroups.length })}</h3>
        <GroupEnrollmentField
          selectedGroups={selectedGroups}
          onChange={setSelectedGroups}
          disabled={isSubmitting}
        />
      </div>

      <div className="with-border rounded-2xl p-4 lg:p-6 space-y-4">
        <h3>{t("members.memberNotes")}</h3>
        <Textarea
          label={t("members.notesOptional")}
          name="member_notes"
          id="member_notes"
          placeholder={t("members.notesPlaceholder")}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      <div className="flex items-start gap-3">
        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          {isSubmitting
            ? t(isEditMode ? "common.saving" : "common.creating")
            : t(isEditMode ? "common.saveChanges" : "members.createMember")}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
};

export default MemberForm;
